import React, { ReactNode } from 'react'

import { AccordionItem } from '~/components/accordion'
import UspBadge from '~/components/usp-badge'

import styles from './styles.module.css'

interface ISection {
  title?: string
  icon?: ReactNode
  header?: string
  subHeader?: string
  body: Array<string>
  badge?: string
  badgeIcon?: ReactNode
}

interface SectionProps {
  section: ISection
  headerExtraMargin?: boolean
  broadbandType?: boolean
  accordion?: boolean
  stacked?: boolean
  largeBody?: boolean
}

interface SectionsProps {
  sections: Array<ISection>
  border?: boolean
  headerExtraMargin?: boolean
  broadbandType?: boolean
  accordion?: boolean
  largeBody?: boolean
  noMargin?: boolean
}

interface TitleSectionProps {
  content: {
    title: string
    amount?: string
    text?: string
  }
  boldAmount?: boolean
  boldTitle?: boolean
  smallHeader?: boolean
  border?: boolean
  noMargin?: boolean
  children?: ReactNode
}

export const Section = ({
  section,
  headerExtraMargin = false,
  broadbandType = false,
  accordion = false,
  stacked = false,
  largeBody = false
}: SectionProps) => {
  const { icon, header, subHeader, body, title, badge, badgeIcon } = section
  return (
    <div className={styles.sectionContainer} data-is-broadband-type={broadbandType} data-is-stacked={stacked}>
      {title && !accordion && <p className={styles.sectionTitle}>{title}</p>}
      {icon && (
        <span className={styles.icon} data-is-broadband-type={broadbandType}>
          {icon}
        </span>
      )}
      {badge && <UspBadge text={badge} icon={badgeIcon} variant='blue-primary' />}
      <div className={styles.section}>
        {header && (
          <p className={styles.sectionHeader} data-extra-margin={headerExtraMargin}>
            {header}
          </p>
        )}
        {body.map((text, index) => {
          return (
            <p
              className={styles.sectionBody}
              data-is-broadband-type={broadbandType}
              data-large-body={largeBody}
              key={index}
            >
              {index === 0 ? <span className={styles.sectionSubHeader}>{subHeader}</span> : null}
              {text}
            </p>
          )
        })}
      </div>
    </div>
  )
}

export const Sections = ({
  sections,
  border = false,
  headerExtraMargin = false,
  broadbandType = false,
  accordion = false,
  largeBody = false,
  noMargin = false
}: SectionsProps) => (
  <div
    className={styles.sections}
    data-is-broadband-type={broadbandType}
    data-has-border={border}
    data-no-margin={noMargin}
  >
    {sections.map((section, index) => {
      return accordion ? (
        <AccordionItem title={section.title} isOpen={false} key={index} noPadding noBorderWhenOpen smallTitle>
          <Section section={section} headerExtraMargin={headerExtraMargin} broadbandType={broadbandType} accordion />
        </AccordionItem>
      ) : (
        <Section
          section={section}
          headerExtraMargin={headerExtraMargin}
          broadbandType={broadbandType}
          largeBody={largeBody}
          key={index}
        />
      )
    })}
  </div>
)

export const TitleSection = ({
  content,
  boldAmount = false,
  boldTitle = false,
  smallHeader = false,
  border = false,
  noMargin = false,
  children
}: TitleSectionProps) => {
  const { title, amount, text } = content
  return (
    <div className={styles.sections} data-has-border={border} data-no-margin={noMargin}>
      <div className={styles.titleSectionHeaderContainer} data-no-margin={noMargin || text || children}>
        <h6 className={styles.titleSectionHeader} data-is-bold={boldTitle} data-is-small={smallHeader}>
          {title}
        </h6>
        <span className={styles.titleSectionAmount} data-is-bold={boldAmount} data-is-small={smallHeader}>
          {amount}
        </span>
      </div>
      {text && <p className={styles.sectionBody}>{text}</p>}
      {children}
    </div>
  )
}

export default { Section, Sections, TitleSection }
